// src/api/errors.ts
/**
 * Reading an API error the way this API actually writes one: an HTTP status, a machine-readable
 * `error` code, and a Slovak sentence in `message`.
 *
 * WHY THIS MODULE IMPORTS NOTHING, AND MUST KEEP IMPORTING NOTHING.
 *
 * These predicates are the part of the error handling worth unit-testing, and CRA 5 runs the tests
 * on jest 27, whose resolver picks the ESM entry out of axios 1.7.8's `exports` map and then cannot
 * parse it. package.json maps `^axios$` to the CommonJS build so the modules that genuinely need an
 * HTTP client still load under jest — but that mapping is an escape hatch for those modules, not a
 * licence to make every predicate depend on axios. The store is the second trap: importing it reads
 * localStorage and constructs the whole Redux tree as a side effect, so a file that pulls it in can
 * no longer be exercised as a plain function. Everything below is a plain function over a plain
 * object, which is why `securityApi`, `sessionErrors`, the step-up modal and the security page can
 * all share it without any of them dragging the others into a test.
 */

/**
 * Machine `error` codes this API sends, mapped to the Slovak sentence a screen should show.
 *
 * Every one of these mirrors, word for word, the `message` the server sends alongside the code
 * (`RateLimitResponses`, `RestAccessDeniedHandler`, `TotpController.badCode`). The map is therefore
 * never a contradiction of the server — it is what keeps the text right when the body is missing,
 * truncated, or written by a jar that predates the code being added.
 *
 * `forbidden` doubles as the reading for Spring Boot's own `/error` envelope, which fills `error`
 * with the reason phrase `"Forbidden"`: `errorCode` lower-cases, so both land on the same sentence.
 * That envelope usually carries an empty `message`, which is exactly the case this map covers.
 *
 * The three session-over codes (`token_expired`, `invalid_token`, `not_authenticated`) are
 * deliberately absent: they are not a message to render, they are a signal to clear the session,
 * and `sessionErrors.ts` owns that decision.
 */
export const ERROR_CODE_MESSAGES: Record<string, string> = {
    rate_limited: 'Priveľa pokusov. Skúste to prosím o chvíľu.',
    invalid_code: 'Neplatný alebo expirovaný kód.',
    mfa_required: 'Táto akcia vyžaduje overenie druhým faktorom.',
    forbidden: 'Na túto akciu nemáte oprávnenie.',
};

/** Shown when the request never reached the server, so there is no status and no body to read. */
export const NETWORK_ERROR_MESSAGE = 'Server neodpovedá. Skontrolujte pripojenie a skúste to znova.';

/** axios `code` values that mean "the request did not complete", as opposed to "the server said no". */
const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ECONNABORTED', 'ECONNREFUSED', 'ETIMEDOUT'];

/** The HTTP status of a failed request, or null when there is none (network failure, thrown Error). */
export function httpStatus(err: any): number | null {
    const status = err?.response?.status;
    return typeof status === 'number' ? status : null;
}

/**
 * The response body as a plain object: an object stays as it is, a JSON string is parsed, anything
 * else is null. Never throws — an unreadable body is simply not evidence of anything.
 *
 * A Blob body (the invoicing CSV/ZIP export and the invoice PDF are fetched with
 * `responseType: 'blob'`, so their *error* bodies arrive as Blobs too) can only be read
 * asynchronously and is reported as null here; `sessionErrors.readErrorBody` is the async reader.
 * ArrayBuffer bodies are likewise left alone rather than decoded.
 */
export function errorBody(err: any): any {
    const data = err?.response?.data;
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        return null;
    }
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }
    if (data && typeof data === 'object') {
        return data;
    }
    return null;
}

/**
 * The machine half of the body, lower-cased so a comparison never has to care whether it came from
 * this app's own handlers (`forbidden`) or Boot's `/error` reason phrase (`Forbidden`).
 */
export function errorCode(err: any): string | null {
    const data = errorBody(err);
    const code = data ? data.error : undefined;
    return typeof code === 'string' && code.trim() ? code.trim().toLowerCase() : null;
}

/**
 * The request never got an answer. Distinguished from a 5xx on purpose: a server that replied with
 * a fault is reachable, and the two need different words on screen.
 */
export function isNetworkError(err: any): boolean {
    if (!err || typeof err !== 'object' || err.response) {
        return false;
    }
    const code = typeof err.code === 'string' ? err.code : '';
    if (NETWORK_ERROR_CODES.indexOf(code) !== -1) {
        return true;
    }
    // axios fills `request` as soon as the call left the browser; no `response` beside it means
    // nothing ever came back. The message check covers XHR's own generic failure.
    return Boolean(err.request) || err.message === 'Network Error';
}

/**
 * Throttled. The status is authoritative — `RateLimitResponses` is the single writer of a 429 in
 * this API and always sends `{status, error: "rate_limited", message}`, but a proxy could answer
 * 429 with nothing at all and the caller still has to back off.
 */
export function isRateLimited(err: any): boolean {
    return httpStatus(err) === 429;
}

/**
 * "You are an admin, enforcement is on, and this session has not passed its second factor."
 *
 * Narrow on purpose: only a 403 whose body names `mfa_required`. A plain `forbidden` 403 means the
 * account may not do this at all and re-verifying would not help, so the two must never be confused
 * — one opens the step-up modal, the other shows a message.
 */
export function isMfaRequired(err: any): boolean {
    return httpStatus(err) === 403 && errorCode(err) === 'mfa_required';
}

/** Not exported: a hasOwnProperty lookup, so a code like `constructor` cannot pull a function off
 *  Object.prototype and have it returned as if it were a message. */
function mappedMessage(code: string | null): string | null {
    if (!code) {
        return null;
    }
    return Object.prototype.hasOwnProperty.call(ERROR_CODE_MESSAGES, code)
        ? ERROR_CODE_MESSAGES[code]
        : null;
}

/**
 * The human half of an error body, never the machine half.
 *
 * Three shapes reach a screen and they disagree about what `error` means. A controller's own handler
 * sends `{error: "<Slovak sentence>"}` — there `error` IS the message. The auth entry point sends
 * `{status, error: "token_expired", message: "<Slovak sentence>"}` and Boot's `/error` sends
 * `{timestamp, status, error: "Forbidden", message, path}`; in both of those `error` is a code or an
 * English reason phrase. Reading `error` first is how `token_expired` got rendered verbatim in an
 * all-Slovak UI — and, because `/error` always fills `error` in, it also made every Slovak fallback
 * on the campaigns page unreachable. (Copied from `pages/Campaigns.tsx`, which is where that bug was
 * diagnosed; this module is now the one place the reasoning lives.)
 *
 * Precedence, and why the code outranks `message`: the mapped sentence is byte-identical to the
 * `message` the server sends beside that code today, so preferring it changes no wording — it only
 * decides what happens when the body is absent, truncated, or written by an older jar. A 403 from
 * Boot's `/error` dispatch, for instance, carries `error: "Forbidden"` and an empty `message`, and
 * without the map the screen would fall through to a generic fallback for a case we can name
 * exactly. Codes the map does not know (`token_expired` and friends) fall straight through to
 * `message`, so nothing renders a machine code.
 *
 * The status is deliberately never read here. `RestAuthenticationEntryPoint` answers 403 rather than
 * 401 today (installed mobile builds log out on 403 and ignore 401, and there is no OTA channel to
 * take that back), and this function has to keep working across that revert.
 */
export function errText(err: any, fallback: string): string {
    if (isNetworkError(err)) {
        return NETWORK_ERROR_MESSAGE;
    }
    const mapped = mappedMessage(errorCode(err));
    if (mapped) {
        return mapped;
    }
    const data = errorBody(err);
    if (data) {
        const message = typeof data.message === 'string' ? data.message.trim() : '';
        if (message) {
            return message;
        }
        // Sole field: that shape only comes from a controller's own handler, where it is prose.
        const sole = typeof data.error === 'string' ? data.error.trim() : '';
        if (sole && Object.keys(data).length === 1) {
            return sole;
        }
    }
    return fallback;
}
